import React, { useCallback, useEffect, useRef, useState } from "react";
import PdfButton from "../../ui/PdfButton.jsx";
import PalletCard from "../salesOrders/PalletCard.jsx";
import FinalizeSheet from "./FinalizeSheet.jsx";
import WorkingAsChip from "../../../features/operatorSession/WorkingAsChip";
import LoadTruckModal from "../../../features/shippingHub/LoadTruckModal";
import { PartDetailModal } from "../../../components/PartDetailModal.jsx";
import { PrepareShipment } from "../../../components/salesOrders/PrepareShipment.jsx";
import usePalletChecks from "../../../features/palletCheck/usePalletChecks";
import { openProblemsFor } from "../../../features/palletCheck/palletCheckData";
import { labelStation } from "../../../features/print/labelStation";
import { loadOrderPlan } from "../../../features/salesOrders/soData";
import { palletSummary, palletSummaryText } from "../../../features/salesOrders/palletRules";
import { planHasHoldingPallets } from "../../../features/salesOrders/holdingPallets";
import {
  confirmPickup, confirmPickupAll, packFromHoldingPallet, printLabelsAt, promoteHoldingPallet, queueStatusLabel,
  reprintBoxLabel, reprintPalletLabels, sealBox, sealPallet, shipPallet, stagePallet, STATUS_LABELS,
} from "../../../features/salesOrders/palletActions";
import {
  bulkReadyOffer, combinedManifestDocument, createEmptyPallet, finalizeOffer, finalizeSentResult, findablePallets,
  findPlanPallets, manualAssign, needsFinalizeNotice, palletStatusCounts, pickupAllOffer, printAllPartLabels,
  printPartLabels, sealedPallets, shipAllReady, stageAllReady,
} from "../../../features/salesOrders/planActions";
import {
  deliveryLoadsOn, deliveryWaitingPallets, loadTruckOrderFromPlan, startDeliveryQr,
} from "../../../features/shippingHub/deliverySigning";
import { startSignedPickup } from "../../../features/shippingHub/pickupSigning";
import { fulfillmentText } from "../../../features/shippingHub/soBoard";
import { absoluteUrl } from "../../../features/signing/signRequestsApi";
import "./guide.css";

// One order's package guide on a phone (Shipping hub "Open package guide"; ?so=&pallet=; Mike 2026-10-29: a
// phone-built guide). What the desktop guide's OPERATOR mode offers (features/packageGuide/guideMode.js): the packing
// plan with every pallet (status, pieces, boxes, beacon; opened on `palletId`), seal box / pallet, ship (Load truck for a
// delivery order with delivery signing on), mark ready for pickup, confirm pickup (the customer signs when
// shipping_signing.pickup is on), Ship all / Mark all ready, reprints and status labels, part labels (one pallet / all
// sealed), the combined manifest, Find (all / one), Prepare shipment for holding pallets, the delivery signing QR and
// the finalize email. Office tools (Admin PIN: unseal, Move/Merge, container type, edit plan, positions, reopen, clear
// WIP, plan create / commit / refresh, routing slips) stay on a computer.
// Every action is the desktop's (features/salesOrders/palletActions + planActions, shippingHub/*); labels print at
// `ui.labelStation` through the shop print queue; PDFs are offered from a tap (PdfButton). Layout only.
//
// Props: apiBase, salesOrderId, palletId (open on it, or null), order (the so-board row, or null), ui (the Working-as
// phone ui: useShippingUi), signing (GET /features shipping_signing), backLabel, onClose().

const STATUS_ORDER = ["Building", "Ready", "Staged", "Shipped"];
const PLAN_STATUS = { ReadyForPickup: "Ready for pickup" };

function Spin({ on, children }) {
  return <>{on && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}{children}</>;
}

const soundsBad = (text) => /failed|couldn't|could not|refused|not sent|⚠/i.test(String(text || ""));

export default function PackageGuideScreen({ apiBase, salesOrderId, palletId = null, order = null, ui, signing = {}, backLabel, onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState(null); // {text, bad}
  const [busy, setBusy] = useState(null);
  const [labelsOffer, setLabelsOffer] = useState(null);
  const [docOffer, setDocOffer] = useState(null);
  const [finalize, setFinalize] = useState(null); // {kind, pin, preActionBits, batchManifestUrl?}
  const [loadTruck, setLoadTruck] = useState(null); // {palletIds | null}
  const [inspectedPartId, setInspectedPartId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const palletChecks = usePalletChecks(plan ? plan.plan_id : null);
  const readSeq = useRef(0);
  const focusedFor = useRef(null);

  const reloadPlan = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    try {
      const p = await loadOrderPlan(salesOrderId);
      if (seq !== readSeq.current) return;
      setPlan(p);
      setLoadError("");
    } catch (err) {
      if (seq === readSeq.current) setLoadError(err.message || String(err));
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [salesOrderId]);

  useEffect(() => {
    setPlan(null); setMessage(null); setLabelsOffer(null); setDocOffer(null); setFinalize(null);
    reloadPlan();
    return () => { readSeq.current += 1; };
  }, [reloadPlan]);

  // open on the scanned pallet once per link: scroll to it, mark it for a few seconds
  useEffect(() => {
    if (!palletId || !plan) return undefined;
    const key = `${salesOrderId}:${palletId}`;
    if (focusedFor.current === key) return undefined;
    if (!(plan.pallets || []).some((p) => String(p.pallet_id) === String(palletId))) return undefined;
    focusedFor.current = key;
    setHighlightId(Number(palletId));
    const scroll = setTimeout(() => {
      const el = document.getElementById(`so-pallet-card-${palletId}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    const clear = setTimeout(() => setHighlightId(null), 4000);
    return () => { clearTimeout(scroll); clearTimeout(clear); };
  }, [palletId, plan, salesOrderId]);

  const printer = labelStation(ui.labelStation).printer;

  // --- the shared actions with this screen's state ---------------------------------------------
  const io = (key) => ({
    apiBase,
    ui,
    setBusy: (on) => setBusy(on ? key : null),
    // progress lines while an action runs ("Connection dropped… still working"); its result sets the final one
    // the shared actions say failures in words ("… failed: …", "⚠ … NOT sent"): show those in red
    setMessage: (text) => setMessage(text ? { text, bad: soundsBad(text) } : null),
    reloadPlan,
    openFinalize: (f) => setFinalize(f),
  });

  const finish = (r) => {
    // backed out (a "No", a closed PIN sheet): leave what is on screen
    if (!r || (!r.done && !r.message)) return;
    setLabelsOffer(r.labels || null);
    setDocOffer(r.document || null);
    if (r.message) setMessage({ text: r.message, bad: !r.done });
    else setMessage(null);
  };
  const run = async (key, action) => {
    if (busy) return null;
    const r = await action(io(key));
    finish(r);
    return r;
  };

  const signedPickup = async (palletIds, key) => {
    setMessage(null);
    const r = await startSignedPickup({ apiBase, plan, palletIds, ui, setBusy: (on) => setBusy(on ? key : null) });
    if (r && r.outcome) reloadPlan();
    finish(r);
  };

  const onAction = (key, args) => {
    const pallet = args.pallet;
    const pid = pallet?.pallet_id;
    if (key === "sealBox") {
      return run(`sealBox_${args.box.box_id}`, (x) => sealBox({ plan, palletId: args.box.pallet_id, boxId: args.box.box_id, boxLabel: args.label, ...x }));
    }
    if (key === "reprintBoxLabel") {
      const owner = (plan.pallets || []).find((p) => (p.boxes || []).some((b) => b.box_id === args.box.box_id));
      return run(`reprintBox_${args.box.box_id}`, (x) => reprintBoxLabel({ shipmentGroupId: owner?.shipment_group_id, shipmentBoxId: args.box.shipment_box_id, boxLabel: args.label, ...x }));
    }
    if (key === "seal") return run(`seal_${pid}`, (x) => sealPallet({ plan, palletId: pid, ...x }));
    if (key === "ship") {
      // a delivery order never leaves with nothing to sign: Load truck (its code on the printed manifest)
      if (deliveryLoadsOn(plan, signing)) { setMessage(null); setLoadTruck({ palletIds: [pid] }); return null; }
      return run(`ship_${pid}`, (x) => shipPallet({ plan, palletId: pid, ...x }));
    }
    if (key === "stage") return run(`stage_${pid}`, (x) => stagePallet({ plan, palletId: pid, ...x }));
    if (key === "confirmPickup") {
      if (signing.pickup) return signedPickup([pid], `confirmPickup_${pid}`);
      return run(`confirmPickup_${pid}`, (x) => confirmPickup({ plan, palletId: pid, ...x }));
    }
    if (key === "reprintLabels") {
      return run(`reprintLabels_${pid}`, (x) => reprintPalletLabels({ shipmentGroupId: pallet.shipment_group_id, palletNum: pallet.pallet_num, palletName: args.name, ...x }));
    }
    if (key === "statusLabel") {
      const { kind, pdfPath } = STATUS_LABELS.pallet(pid);
      return run(`statusLabel_${pid}`, (x) => queueStatusLabel({ kind, idLabel: args.name, pdfPath, trigger: "phone_package_guide", station: ui.labelStation, ...x }));
    }
    return null;
  };

  const printOffered = async () => {
    if (busy) return; // one thing at a time: a print must not end another action's busy state
    const offer = labelsOffer;
    setBusy("labels");
    try {
      const r = await printLabelsAt({ station: ui.labelStation, path: offer.path, what: offer.what, trigger: "phone_package_guide" });
      setMessage({ text: r.message, bad: false });
      setLabelsOffer(null);
    } catch (err) {
      setMessage({ text: `Printing ${offer.what} failed: ${err.message || String(err)}`, bad: true });
    } finally {
      setBusy(null);
    }
  };

  const find = async (palletIds, key) => {
    if (busy) return;
    setBusy(key);
    try { await findPlanPallets({ plan, palletIds, reloadPlan }); } finally { setBusy(null); }
  };

  const loadTruckClosed = (r) => {
    setLoadTruck(null);
    if (!r || !r.loaded) return;
    setLabelsOffer(null);
    setMessage({ text: `${plan?.order_number || "The order"}: loaded on the truck.`, bad: false });
    setDocOffer(r.manifest
      ? { url: absoluteUrl(apiBase, r.manifest), filename: `Manifest_${plan?.order_number || "order"}.pdf`, what: "Manifest with the signing QR (driver + customer copy)" }
      : null);
    reloadPlan();
  };

  const deliveryQr = async () => {
    setMessage(null);
    const r = await startDeliveryQr({
      apiBase, ui, planId: plan.plan_id, orderNumber: plan.order_number, customerName: plan.customer_name || null,
      setBusy: (on) => setBusy(on ? "delivery_qr" : null),
    });
    if (r.message) setMessage({ text: r.message, bad: !r.done });
    if (r.outcome) reloadPlan();
  };

  const finalizeSent = (responseBody, { notify }) => {
    const r = finalizeSentResult({
      apiBase, plan, kind: finalize.kind, preActionBits: finalize.preActionBits, responseBody, notify, opensPdf: false,
    });
    setFinalize(null);
    setLabelsOffer(null);
    setMessage({ text: r.message, bad: soundsBad(r.message) });
    setDocOffer(r.document);
    reloadPlan();
  };

  // --- layout --------------------------------------------------------------------------------
  const soLabel = (plan && plan.order_number) || (order && order.order_number) || `SO #${salesOrderId}`;
  const customer = (plan && plan.customer_name) || (order && order.customer_name) || "";
  const fulfillment = (plan && plan.fulfillment_type) || (order && order.fulfillment_type) || null;

  const head = (
    <>
      <button type="button" className="btn btn-link px-0 mb-2" onClick={onClose}>
        <i className="fas fa-chevron-left me-1" aria-hidden="true" />{backLabel}
      </button>
      <section className="jq-card" data-testid="phone-guide-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{soLabel}</span>
          {plan && <span className="jq-tag">{PLAN_STATUS[plan.status] || plan.status}</span>}
        </div>
        {customer && <div className="fw-semibold">{customer}</div>}
        {fulfillment && <div className="jq-muted">{fulfillmentText(fulfillment)}</div>}
        <div className="mt-2"><WorkingAsChip ui={ui} /></div>
      </section>
    </>
  );

  if (!plan) {
    return (
      <div className="jq-guide" data-testid="phone-ship-guide">
        {head}
        {loading && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading the packing plan…</div>}
        {!loading && loadError && (
          <div className="jq-note jq-note-danger" role="alert">
            Couldn't read the packing plan: {loadError}
            <button type="button" className="btn btn-outline-danger w-100 mt-2" onClick={reloadPlan}>Try again</button>
          </div>
        )}
        {!loading && !loadError && (
          <div className="jq-note jq-note-info" data-testid="phone-guide-no-plan">No packing plan for {soLabel} yet. The office creates it.</div>
        )}
      </div>
    );
  }

  const counts = palletStatusCounts(plan);
  const bulk = bulkReadyOffer(plan);
  const pickupAll = pickupAllOffer(plan);
  const fin = finalizeOffer(plan);
  const open = plan.status === "Committed" || plan.status === "Fulfilling";
  const sealed = open ? sealedPallets(plan) : [];
  const findable = open ? findablePallets(plan) : [];
  const waiting = signing.delivery && plan.fulfillment_type === "delivery" ? deliveryWaitingPallets(plan).length : 0;
  const pallets = (plan.pallets || []).filter((p) => p.status !== "Consolidated");

  const bulkLabel = bulk && (bulk.isPickup
    ? (bulk.allReady ? `Mark all ${bulk.readyCount} pallet(s) ready for pickup: close out` : `Mark ${bulk.readyCount} pallet(s) ready for pickup`)
    : (bulk.allReady ? `Ship all ${bulk.readyCount} pallet(s): close out` : `Ship ${bulk.readyCount} ready pallet(s)`));
  const onBulk = () => {
    if (!bulk) return;
    if (bulk.isPickup) {
      run("bulk", (x) => stageAllReady({ plan, salesOrderId, readyCount: bulk.readyCount, ...x }));
    } else if (deliveryLoadsOn(plan, signing)) {
      setMessage(null); setLoadTruck({ palletIds: null });
    } else {
      run("bulk", (x) => shipAllReady({ plan, salesOrderId, readyCount: bulk.readyCount, ...x }));
    }
  };
  const onPickupAll = () => {
    if (signing.pickup) signedPickup(null, "pickupAll");
    else run("pickupAll", (x) => confirmPickupAll({ plan, stagedCount: pickupAll.stagedCount, ...x }));
  };

  return (
    <div className="jq-guide" data-testid="phone-ship-guide">
      {head}

      <section className="jq-card" data-testid="phone-guide-counts">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => (
            <span key={s} className="jq-tag">{counts[s]} {s === "Staged" ? "awaiting pickup" : s.toLowerCase()}</span>
          ))}
          <span className="jq-muted">{counts.total} pallet{counts.total === 1 ? "" : "s"}</span>
          <button type="button" className="btn btn-outline-secondary ms-auto" aria-label="Reload the plan" disabled={loading || !!busy} onClick={() => { setMessage(null); reloadPlan(); }}>
            <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
          </button>
        </div>
      </section>

      {message && (
        <div className={`jq-note ${message.bad ? "jq-note-danger" : "jq-note-info"} d-flex justify-content-between align-items-start`} role="status" data-testid="phone-guide-message">
          <span style={{ whiteSpace: "pre-line" }}>{message.text}</span>
          <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
        </div>
      )}
      {labelsOffer && (
        <div className="jq-card" data-testid="phone-guide-labels-offer">
          <div className="fw-semibold mb-2">{labelsOffer.what}</div>
          <button type="button" className="btn btn-primary btn-lg w-100" disabled={!!busy} onClick={printOffered}>
            <Spin on={busy === "labels"}><i className="fas fa-print me-2" aria-hidden="true" />Print at the {printer}</Spin>
          </button>
          <button type="button" className="btn btn-link w-100" onClick={() => setLabelsOffer(null)}>Not now (print it from a computer)</button>
        </div>
      )}
      {docOffer && <PdfButton url={docOffer.url} filename={docOffer.filename} label={docOffer.what} fetchNow className="btn btn-primary w-100 text-start" testId="phone-guide-doc-offer" />}

      {needsFinalizeNotice(plan) && (
        <div className="jq-note jq-note-warn" data-testid="phone-guide-not-notified">
          The customer has not been notified yet: per-pallet emails are off and the order was never finalized.
        </div>
      )}

      <div className="d-grid gap-2 mb-3" data-testid="phone-guide-plan-actions">
        {bulk && (
          <button type="button" className={`btn btn-lg ${bulk.isPickup ? "btn-warning" : "btn-primary"}`} disabled={!!busy} onClick={onBulk} data-testid="phone-guide-bulk">
            <Spin on={busy === "bulk"}>{bulkLabel}</Spin>
          </button>
        )}
        {pickupAll && (
          <button type="button" className="btn btn-success btn-lg" disabled={!!busy} onClick={onPickupAll} data-testid="phone-guide-pickup-all">
            <Spin on={busy === "pickupAll"}>Customer here: confirm pickup of {pickupAll.stagedCount} pallet(s)</Spin>
          </button>
        )}
        {waiting > 0 && (
          <button type="button" className="btn btn-outline-primary btn-lg" disabled={!!busy} onClick={deliveryQr} data-testid="phone-guide-delivery-qr">
            <Spin on={busy === "delivery_qr"}><i className="fas fa-qrcode me-2" aria-hidden="true" />Delivery signing QR ({waiting} shipped)</Spin>
          </button>
        )}
        {fin && (
          <button type="button" className="btn btn-success btn-lg" disabled={!!busy} data-testid="phone-guide-finalize-open"
            onClick={() => { setMessage(null); setFinalize({ kind: fin.kind, pin: "", preActionBits: [] }); }}>
            <i className="fas fa-flag me-2" aria-hidden="true" />
            {fin.batch ? "Send new-batch manifest (notify customer)" : (fin.kind === "pickup" ? "Finalize pickup (notify customer)" : "Finalize shipment (notify customer)")}
          </button>
        )}
        {plan.status === "Complete" && plan.so_finalized_at && (() => {
          const doc = combinedManifestDocument(plan, apiBase);
          return <PdfButton url={doc.url} filename={doc.filename} label="Combined manifest (every pallet)" testId="phone-guide-combined-manifest" />;
        })()}
        {sealed.length > 0 && (
          <button type="button" className="btn btn-outline-secondary" disabled={!!busy} data-testid="phone-guide-part-labels-all"
            onClick={() => run("partLabelsAll", (x) => printAllPartLabels({ plan, ...x }))}>
            <Spin on={busy === "partLabelsAll"}><i className="fas fa-tags me-2" aria-hidden="true" />Part labels, all {sealed.length} sealed pallet(s), at the {printer}</Spin>
          </button>
        )}
        {findable.length > 0 && (
          <button type="button" className="btn btn-outline-primary" disabled={!!busy} data-testid="phone-guide-find-all" onClick={() => find(null, "findAll")}>
            <i className="fas fa-broadcast-tower me-2" aria-hidden="true" />Find all pallets ({findable.length})
          </button>
        )}
      </div>

      {planHasHoldingPallets(plan) && (
        <div className="jq-guide-prepare" data-testid="phone-guide-prepare">
          <PrepareShipment
            plan={plan}
            busy={busy || ""}
            palletChecks={palletChecks}
            onMoved={reloadPlan}
            onPromote={(id) => run(`promote_${id}`, (x) => promoteHoldingPallet({ plan, palletId: id, ...x }))}
            onManualAssign={(body) => manualAssign({ plan, body, ...io(`assign_${body.part_id}`) })}
            onPackFromHolding={(body) => packFromHoldingPallet({ plan, body, ...io(`assign_${body.part_id}`) })}
            onCreatePallet={(size) => createEmptyPallet({ plan, sizeChoice: size, ...io("new_pallet") })}
            onInspectPart={setInspectedPartId}
            onFind={(id) => find([id], `find_${id}`)}
          />
        </div>
      )}

      <h2 className="jq-section-title mt-3">Pallets</h2>
      {pallets.length === 0 && <div className="jq-muted">No pallets on this plan.</div>}
      {pallets.map((p) => {
        const sum = palletSummary(p);
        const name = p.short_id || `P${p.pallet_num}`;
        const canFind = p.beacon_id && p.status !== "Shipped";
        return (
          <PalletCard key={p.pallet_id} plan={plan} pallet={p} busy={busy} onAction={onAction} printer={printer}
            highlight={highlightId === p.pallet_id} openProblems={openProblemsFor(palletChecks, p.pallet_id)}
            summary={(
              <span>
                {palletSummaryText(sum)}
                {sum.beacon && (
                  <span className="jq-tag ms-2" data-testid={`phone-guide-beacon-${p.pallet_id}`}>
                    {sum.beaconLit && <span className="jq-guide-lit" aria-label="lit" />}{sum.beacon}
                  </span>
                )}
              </span>
            )}>
            {p.shipment_group_id && (
              <button type="button" className="btn btn-outline-secondary" disabled={!!busy} data-testid={`phone-guide-part-labels-${p.pallet_id}`}
                onClick={() => run(`partLabels_${p.pallet_id}`, (x) => printPartLabels({ shipmentGroupId: p.shipment_group_id, palletNum: p.pallet_num, palletName: name, ...x }))}>
                <Spin on={busy === `partLabels_${p.pallet_id}`}><i className="fas fa-tags me-2" aria-hidden="true" />Part labels at the {printer}</Spin>
              </button>
            )}
            {canFind && (
              <button type="button" className="btn btn-outline-primary" disabled={!!busy} data-testid={`phone-guide-find-${p.pallet_id}`}
                onClick={() => find([p.pallet_id], `find_${p.pallet_id}`)}>
                <i className="fas fa-broadcast-tower me-2" aria-hidden="true" />Find {name}
              </button>
            )}
          </PalletCard>
        );
      })}

      {inspectedPartId && <PartDetailModal apiBase={apiBase} partId={inspectedPartId} onClose={() => setInspectedPartId(null)} />}
      {loadTruck && (
        <LoadTruckModal apiBase={apiBase} ui={ui} order={loadTruckOrderFromPlan(plan, loadTruck.palletIds)} onClose={loadTruckClosed} />
      )}
      <FinalizeSheet
        open={!!finalize}
        apiBase={apiBase}
        plan={plan}
        kind={finalize ? finalize.kind : "shipping"}
        pin={finalize ? finalize.pin || "" : ""}
        batchManifest={finalize && finalize.batchManifestUrl
          ? { url: `${apiBase}${finalize.batchManifestUrl}`, filename: `Manifest_${plan.order_number || plan.plan_id}_batch.pdf`, what: "This shipment's manifest" }
          : null}
        ui={ui}
        onClose={() => setFinalize(null)}
        onSent={finalizeSent}
      />
    </div>
  );
}
